import { buildCategoryPathFromTrail, normalizePathname } from './routing'
import type { CatalogProduct, CategoryRouteData } from './types'

const decodeSegmentLabel = (segment: string) =>
  decodeURIComponent(segment)
    .replace(/[-_]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()

const toTitleCase = (value: string) =>
  value.replace(/\w\S*/g, (token) => token.charAt(0).toUpperCase() + token.slice(1))

export const trimText = (value: string | null | undefined, maxLength: number) => {
  const normalized = typeof value === 'string' ? value.trim() : ''
  if (!normalized) return ''
  if (normalized.length <= maxLength) return normalized
  return `${normalized.slice(0, Math.max(0, maxLength - 1)).trim()}...`
}

export const normalizeSlugPath = (slug: string) => {
  const clean = slug.trim().replace(/^\/+|\/+$/g, '')
  return clean ? `/${clean}` : '/'
}

export const pickProductImage = (product: CatalogProduct | null | undefined) =>
  product?.mediaGallery?.find((entry) => typeof entry === 'string' && entry.trim().length > 0) ?? ''

export const pickCategoryImage = (categoryData: CategoryRouteData | null | undefined) =>
  categoryData?.category.imageVariants?.large ||
  categoryData?.category.imageVariants?.medium ||
  categoryData?.category.imageVariants?.small ||
  ''

export const inferProductBrand = (product: CatalogProduct | null | undefined) => {
  const entries = Array.isArray(product?.metadata) ? product.metadata : []
  const brandEntry = entries.find((entry) => {
    const key = entry.key.trim().toLowerCase()
    return key === 'brand' || key === 'marca'
  })
  return brandEntry?.value?.trim() || undefined
}

export const buildProductBreadcrumbs = (canonicalPath: string, productName: string) => {
  const segments = canonicalPath.split('/').filter(Boolean)
  const breadcrumbs: Array<{ name: string; path: string }> = [{ name: 'Home', path: '/' }]

  if (segments[0] === 'products') {
    let cursor = '/products'
    breadcrumbs.push({ name: 'Prodotti', path: cursor })
    for (let index = 1; index < Math.max(segments.length - 1, 1); index += 1) {
      cursor = `${cursor}/${segments[index]}`
      breadcrumbs.push({
        name: toTitleCase(decodeSegmentLabel(segments[index])),
        path: cursor,
      })
    }
    breadcrumbs.push({ name: productName, path: canonicalPath })
    return breadcrumbs
  }

  breadcrumbs.push({ name: productName, path: canonicalPath })
  return breadcrumbs
}

export const buildCategoryBreadcrumbs = (categoryData: CategoryRouteData) => {
  const breadcrumbs: Array<{ name: string; path: string }> = [{ name: 'Home', path: '/' }]
  const trail = categoryData.trail
  let cursor = ''
  trail.forEach((category) => {
    cursor = `${cursor}/${encodeURIComponent(category.slug)}`
    breadcrumbs.push({
      name: category.name,
      path: normalizePathname(cursor),
    })
  })
  return breadcrumbs
}

export const buildPageBreadcrumbs = (title: string, canonicalPath: string) =>
  canonicalPath === '/'
    ? [{ name: 'Home', path: '/' }]
    : [
        { name: 'Home', path: '/' },
        { name: title, path: canonicalPath },
      ]

export const buildCategoryCanonicalPath = (categoryData: CategoryRouteData) =>
  buildCategoryPathFromTrail(categoryData.trail)
